import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  X, MapPin, Phone, Edit, Trash2, Eye, CheckCircle, FolderOpen, FolderPlus, Loader2, Download, Paperclip, Upload, ScanLine
} from "lucide-react";
import IcertiButton from "./IcertiButton";
import { Button } from "../ui/button";
import { catastroUrl } from "../../lib/catastroLink";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { statusColors, EQUIPE_COLORS, EQUIPE_LABELS } from "./planningConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Right-hand detail panel of the Planning: data, documents, tech photos, iCerti and dossier
// actions of the selected appointment. The Planning keeps the list, the selection and the
// edit/delete/create-dossier logic; this panel receives them as props.
const AppointmentDetailPanel = ({
  selectedAppointment, setSelectedAppointment, currentUser, fetchAppointments,
  handleDelete, openEditDialog, setConfirmCreateDossier, setShowTechDataDialog,
}) => {
  const navigate = useNavigate();
  const [extractingDni, setExtractingDni] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docFileRef = useRef(null);

  return (
    <Card className="w-80 shrink-0 overflow-y-auto" data-testid="appointment-detail">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedAppointment.color || '#3b82f6' }} />
            <CardTitle className="text-base">Detalle Cita</CardTitle>
          </div>
          <button onClick={() => setSelectedAppointment(null)} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        {selectedAppointment.ref_cadastre && (
          <div>
            <span className="text-slate-400 text-xs">Referencia Catastral</span>
            <a href={catastroUrl(selectedAppointment.ref_cadastre, API)}
              target="_blank" rel="noreferrer"
              className="font-medium text-blue-600 hover:text-blue-800 hover:underline block"
              data-testid="cadastre-link">
              {selectedAppointment.ref_cadastre}
            </a>
          </div>
        )}
        {selectedAppointment.vendeur && (
          <div>
            <span className="text-slate-400 text-xs">Venta realizada por</span>
            <p className="font-medium text-slate-800">{selectedAppointment.vendeur}</p>
          </div>
        )}
        <div>
          <span className="text-slate-400 text-xs">Equipo técnico</span>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: EQUIPE_COLORS[selectedAppointment.equipe || "equipe1"] }} />
            <span className="font-medium text-slate-800">{EQUIPE_LABELS[selectedAppointment.equipe || "equipe1"]}</span>
          </div>
        </div>
        <div>
          <span className="text-slate-400 text-xs">Estado</span>
          <div className="flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white"
              style={{ backgroundColor: statusColors[selectedAppointment.status] || '#6b7280' }}>
              {selectedAppointment.status}
            </span>
            {selectedAppointment.status === "Instalado" && (
              <Button size="sm" className="h-7 text-xs bg-green-700 hover:bg-green-800" data-testid="validate-appt-btn"
                onClick={async () => {
                  try {
                    await axios.put(`${API}/appointments/${selectedAppointment.id}`, { status: "Validado" });
                    setSelectedAppointment(p => ({ ...p, status: "Validado" }));
                    await fetchAppointments(); // Fix 4: await pour propager les erreurs correctement
                    toast.success("RDV validé !");
                  } catch (err) {
                    if (!axios.isCancel(err)) toast.error("Erreur lors de la validation");
                  }
                }}>
                &#10003;&#10003; Validar
              </Button>
            )}
          </div>
        </div>
        {selectedAppointment.source && (
          <div>
            <span className="text-slate-400 text-xs">Fuente</span>
            <p className="font-medium">{selectedAppointment.source}</p>
          </div>
        )}
        <div>
          <span className="text-slate-400 text-xs">Fecha</span>
          <p className="font-medium">{selectedAppointment.date}</p>
        </div>
        <div>
          <span className="text-slate-400 text-xs">Nombre / Apellido</span>
          <p className="font-medium text-slate-800">{selectedAppointment.client_name}</p>
        </div>
        {selectedAppointment.address && (
          <div>
            <span className="text-slate-400 text-xs">Dirección</span>
            <div className="flex items-start gap-1.5">
              <MapPin size={14} className="text-slate-400 mt-0.5 shrink-0" />
              <p className="font-medium text-slate-700">
                {selectedAppointment.address}
                {(selectedAppointment.city || selectedAppointment.postal_code) && (
                  <span className="text-slate-500">{selectedAppointment.address ? ", " : ""}{selectedAppointment.postal_code} {selectedAppointment.city}</span>
                )}
              </p>
            </div>
          </div>
        )}
        {selectedAppointment.phone && (
          <div>
            <span className="text-slate-400 text-xs">Teléfono</span>
            <div className="flex items-center gap-1.5">
              <Phone size={14} className="text-slate-400 shrink-0" />
              <p className="font-medium">{selectedAppointment.phone}</p>
            </div>
          </div>
        )}
        {selectedAppointment.email && (
          <div>
            <span className="text-slate-400 text-xs">Correo electrónico</span>
            <p className="font-medium">{selectedAppointment.email}</p>
          </div>
        )}
        {selectedAppointment.type_logement && (
          <div>
            <span className="text-slate-400 text-xs">Tipo de vivienda</span>
            <p className="font-medium">{selectedAppointment.type_logement}</p>
          </div>
        )}
        <div>
          <span className="text-slate-400 text-xs">Hora</span>
          <p className="font-medium">{selectedAppointment.start_time}</p>
        </div>
        {selectedAppointment.notes && (
          <div>
            <span className="text-slate-400 text-xs">Notas</span>
            <p className="text-slate-600">{selectedAppointment.notes}</p>
          </div>
        )}
        {/* Documentos adjuntos — attach/download any file on the appointment
            itself, works before a dossier exists */}
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="appointment-documents">
          <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1.5 mb-2">
            <Paperclip size={14} /> Documentos adjuntos
          </span>
          {(selectedAppointment.documentos_adjuntos || []).map((doc) => (
            <div key={doc.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-200 last:border-0">
              <span className="text-sm text-slate-700 truncate" title={doc.nombre_archivo}>{doc.nombre_archivo}</span>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  data-testid={`download-doc-${doc.id}`}
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/appointments/${selectedAppointment.id}/documentos/${doc.id}`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = doc.nombre_archivo;
                      document.body.appendChild(link);
                      link.click();
                      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 200);
                    } catch {
                      toast.error("Error al descargar el documento");
                    }
                  }}
                >
                  <Download size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500"
                  data-testid={`delete-doc-${doc.id}`}
                  onClick={async () => {
                    if (!window.confirm(`¿Eliminar "${doc.nombre_archivo}"?`)) return;
                    try {
                      await axios.delete(`${API}/appointments/${selectedAppointment.id}/documentos/${doc.id}`);
                      setSelectedAppointment((p) => ({ ...p, documentos_adjuntos: (p.documentos_adjuntos || []).filter((d) => d.id !== doc.id) }));
                      toast.success("Documento eliminado");
                    } catch {
                      toast.error("Error al eliminar el documento");
                    }
                  }}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
          <input
            type="file"
            ref={docFileRef}
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setUploadingDoc(true);
              const formData = new FormData();
              formData.append("file", file);
              try {
                const res = await axios.post(`${API}/appointments/${selectedAppointment.id}/documentos`, formData, {
                  headers: { "Content-Type": "multipart/form-data" },
                });
                setSelectedAppointment((p) => ({ ...p, documentos_adjuntos: [...(p.documentos_adjuntos || []), res.data] }));
                toast.success("Documento adjuntado");
              } catch {
                toast.error("Error al adjuntar el documento");
              } finally {
                setUploadingDoc(false);
                e.target.value = "";
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2"
            disabled={uploadingDoc}
            data-testid="attach-doc-btn"
            onClick={() => docFileRef.current?.click()}
          >
            {uploadingDoc ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Upload size={14} className="mr-2" />}
            Adjuntar Documento
          </Button>
        </div>
        {selectedAppointment.dossier_id && currentUser?.role !== "commercial" && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2"
            data-testid="go-to-dossier"
            onClick={async () => {
              const dossierId = selectedAppointment.dossier_id;
              setSelectedAppointment(null);
              // Same "dossiers" collection holds both RES020 and RES022 --
              // check fiche_type before deciding which detail route to use,
              // instead of always assuming RES020.
              try {
                const res = await axios.get(`${API}/dossiers/${dossierId}`);
                const isRes022 = res.data?.fiche_type === "RES022";
                navigate(isRes022 ? `/dossier-res022/${dossierId}` : `/dossier/${dossierId}`);
              } catch {
                navigate(`/dossier/${dossierId}`);
              }
            }}
          >
            <FolderOpen size={14} className="mr-2" /> Ver Expediente
          </Button>
        )}
        {/* Tech data indicator */}
        {selectedAppointment.tech_validated && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="tech-data-received">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-2">
              <CheckCircle size={16} /> Datos del técnico recibidos
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={() => setShowTechDataDialog(selectedAppointment.id)} data-testid="view-tech-data">
              <Eye size={14} className="mr-2" /> Ver fotos y firmas
            </Button>
            {/* Read name + DNI from the DNI photo - works before a dossier exists */}
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2"
              disabled={extractingDni}
              onClick={async () => {
                setExtractingDni(true);
                try {
                  const res = await axios.post(`${API}/appointments/${selectedAppointment.id}/extract-dni`);
                  setSelectedAppointment((p) => p && ({ ...p, client_name: res.data.client_name, dni_extraido: { nombre_completo: res.data.nombre_completo, dni: res.data.dni } }));
                  fetchAppointments();
                  toast.success(`DNI extraído: ${res.data.nombre_completo} - ${res.data.dni}`);
                } catch (error) {
                  toast.error(error.response?.data?.detail || "Error al extraer DNI");
                } finally {
                  setExtractingDni(false);
                }
              }}
              data-testid="extract-dni-appointment"
            >
              {extractingDni ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ScanLine size={14} className="mr-2" />}
              Extraer DNI
            </Button>
            {currentUser?.role !== "commercial" && (
              <IcertiButton
                appointment={selectedAppointment}
                onChanged={(pedido) => {
                  fetchAppointments();
                  if (pedido) {
                    setSelectedAppointment((p) => p && ({
                      ...p,
                      status: p.status === "Anulado" ? p.status : "Validado",
                      icerti: { ...(p.icerti || {}), pedido },
                    }));
                  }
                }}
              />
            )}
            {/* Create dossier from appointment */}
            {!selectedAppointment.dossier_id && currentUser?.role !== "commercial" && (
              <Button 
                variant="default" 
                size="sm" 
                className="w-full mt-2 bg-blue-600 hover:bg-blue-700" 
                onClick={() => setConfirmCreateDossier(true)}
                data-testid="create-dossier-from-appointment"
              >
                <FolderPlus size={14} className="mr-2" /> Crear Expediente
              </Button>
            )}
          </div>
        )}
        {!selectedAppointment.tech_validated && (
          <div className="mt-3 p-3 bg-slate-50 border border-slate-400 rounded-lg text-center" data-testid="tech-data-pending">
            <p className="text-xs text-slate-400">Sin datos del técnico</p>
          </div>
        )}
        {selectedAppointment.status === "Validado" && currentUser?.role !== "admin" ? (
          <p className="pt-2 text-xs text-center text-slate-500">&#128274; Cita validada: solo el administrador puede modificarla</p>
        ) : (
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditDialog(selectedAppointment)} data-testid="edit-appointment">
            <Edit size={14} className="mr-1" /> Modificar
          </Button>
          <Button variant="destructive" size="sm" className="flex-1" onClick={() => { if (window.confirm("¿Seguro que quieres eliminar esta cita?")) handleDelete(selectedAppointment.id); }} data-testid="delete-appointment">
            <Trash2 size={14} className="mr-1" /> Eliminar
          </Button>
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AppointmentDetailPanel;
